/**
 * One Tool (SMSWiser) table: sort + text filter + group-by (with tool-metric
 * subtotals), reusing the shared StatsTable. Source-agnostic — fed by a single
 * drop's block or by the aggregated multi-drop view.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useMemo, useState } from 'react';

import {
  StatsTable,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';

import { TOOL_COLUMN_DEFS } from '../shared/tool-columns';
import { buildToolGroups, type ToolGroupOption } from '../shared/tool-grouping';
import { ToolStatsToolbar } from './ToolStatsToolbar';

interface ToolStatsTableProps {
  rows: GenericStatsRow[];
  totals: Record<string, number>;
  groupOptions: ToolGroupOption[];
  firstColumnLabel: string;
  currency?: string;
  defaultGroupBy?: string;
  emptyMessage?: string;
}

type Sort = { field: string; dir: 'asc' | 'desc' } | null;

function sortRows(rows: GenericStatsRow[], sort: Sort): GenericStatsRow[] {
  if (!sort) return rows;
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.field === 'label') return a.label.localeCompare(b.label) * factor;
    return ((a.voluum?.[sort.field] ?? 0) - (b.voluum?.[sort.field] ?? 0)) * factor;
  });
}

export const ToolStatsTable: FC<ToolStatsTableProps> = ({
  rows: inputRows,
  totals,
  groupOptions,
  firstColumnLabel,
  currency = 'USD',
  defaultGroupBy = 'none',
  emptyMessage = 'No data for this dimension.',
}) => {
  const [groupBy, setGroupBy] = useState(defaultGroupBy);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>(null);
  const [selectedRows, setSelectedRows] = useState<GenericStatsRow[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? inputRows.filter((r) => r.searchText.includes(q)) : inputRows;
  }, [inputRows, search]);
  const rows = useMemo(() => sortRows(filtered, sort), [filtered, sort]);
  const groupOpt = groupOptions.find((o) => o.value === groupBy) ?? groupOptions[0]!;
  const groups = useMemo(
    () => (groupOpt.value === 'none' ? null : buildToolGroups(filtered, groupOpt.keyOf, sort)),
    [groupOpt, filtered, sort],
  );

  const onSortClick = (field: string) =>
    setSort((prev) =>
      prev?.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'desc' },
    );
  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (inputRows.length === 0) {
    return <p className="px-1 py-6 text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-3">
      <ToolStatsToolbar
        groupOptions={groupOptions}
        groupBy={groupOpt.value}
        onGroupByChange={setGroupBy}
        search={search}
        onSearchChange={setSearch}
        rowCount={rows.length}
      />
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="overflow-auto">
          <StatsTable
            rows={rows}
            totals={totals}
            groups={groups}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleGroup}
            totalRowCount={rows.length}
            currency={currency}
            columnDefs={TOOL_COLUMN_DEFS}
            sort={sort}
            onSortClick={onSortClick}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            renderFirstCell={(row) => (
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-medium text-foreground">{row.label}</span>
                {row.subLabel ? (
                  <span className="truncate text-xs text-muted-foreground">{row.subLabel}</span>
                ) : null}
              </div>
            )}
            firstColumnLabel={firstColumnLabel}
            firstColumnSortKey="label"
            rowUnitLabel="row"
          />
        </div>
      </div>
    </div>
  );
};

export default ToolStatsTable;
